import {Component, DestroyRef, inject, OnInit, signal} from "@angular/core";
import {
    AbstractControl,
    ControlContainer,
    FormArray, FormControl, UntypedFormBuilder as FormBuilder,
    UntypedFormGroup as FormGroup,
    Validators
} from "@angular/forms";
import {Utils} from "../../../../../core/helpers/Utils";
import {BusinessType, ServiceMetadataCategory, WizardUtils,} from "../../shared/WizardHelpers";
import {Const, ResourceType} from "../../../../../core/helpers/Const";
import {PAYLOAD_FORM_CONTROLS, PAYLOAD_FORM_GROUPS} from "../unified-create-suite-dialog.component";
import {DialogRef} from "@fundamental-ngx/core/dialog";
import {NestedValuePipe} from "../../../../../shared/pipes/nested-value/nested-value.pipe";
import {CustomValidators} from "../../../../../core/helpers/CustomValidators";
import {KeyValue} from "@angular/common";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AppConfigService} from "../../../../../service/services/misc/app-config/app-config.service";

export const STEP_SETUP_FORM_GROUPS = {
    STEP_SETUP: "step1",
    SERVICE_METADATA: "serviceMetadata",
    RESOURCE_TYPE_DEFINITION: "resourceTypeDefinition"
};
export const STEP_SETUP_FORM_CONTROLS = {

    BUSINESS_TYPES: "businessTypes",
    BUSINESS_TYPE: {
        TYPE: "type",
        TITLE: "title",
        CODE: "code",
        NAME: "name"
    },
    RESOURCE_TYPE_DEFINITION: {
        NAME: "name",
        GROUP: "group",
        VERSION: "version",
        TYPE: "type",
        PLURAL: "plural",
        SINGULAR: "singular"
    },
    SERVICE_METADATA: {
        NAME: "name",
        SUITE_NAME: "suiteName",
        DISPLAY_NAME: "displayName",
        DESCRIPTION: "description",
        TENANT_PRODUCT_TYPE: "tenantProductType",
        DOCUMENTATION_URL: "documentationUrl",
        ICON_URL: "iconUrl",
        PACKAGE: "package",
        CATEGORY: "category"
    }
};

export const PRODUCT_BLUEPRINT_NAME_PATTERN =
    /^(?=.*[a-z]$)[a-z](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*$/;

export const TENANT_PRODUCT_TYPE_PATTERN =
    /^[a-zA-Z0-9 :.,'"\-_#~!?@&$%+;[\]]*$/;

export interface BusinessTypeProperties {
    title: string;
    code: string;
    name: string;
}

const BUSINESS_TYPE_MAP: Record<BusinessType, BusinessTypeProperties> = {
    [BusinessType.PRODUCTION]: {title: 'Production', code: 'ZH421', name: 'prod'},
    [BusinessType.DEVELOPMENT]: {title: 'Development', code: 'ZH527', name: 'dev'},
    [BusinessType.TEST]: {title: 'Test', code: 'ZH526', name: 'test'},
    [BusinessType.OTHER]: {title: null, code: null, name: null}
};

const ALL_BUSINESS_TYPE_OPTIONS: KeyValue<string, BusinessType>[] = [
    {key: "Production", value: BusinessType.PRODUCTION},
    {key: "Development", value: BusinessType.DEVELOPMENT},
    {key: "Test", value: BusinessType.TEST},
    {key: "Other", value: BusinessType.OTHER}
];


type CategoryOption = { key: string; label: string };

@Component({
    selector: "app-unified-suite-step-setup",
    templateUrl: "./unified-suite-step-setup.component.html",
    styleUrls: ["./unified-suite-step-setup.component.scss"],
    standalone: false
})
export class UnifiedSuiteStepSetupComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    readonly Utils = Utils;
    readonly Const = Const;
    readonly STEP_SETUP_FORM_CONTROLS = STEP_SETUP_FORM_CONTROLS;
    readonly STEP_SETUP_FORM_GROUPS = STEP_SETUP_FORM_GROUPS;
    public form: FormGroup;
    public optionSets = signal<KeyValue<string, BusinessType>[][]>([]);

    readonly dialogRef = inject(DialogRef);
    readonly controlContainer = inject(ControlContainer);
    readonly fb = inject(FormBuilder);
    readonly nestedValuePipe = inject(NestedValuePipe);
    readonly appConfigService = inject(AppConfigService);

    public categoryOptions = signal<CategoryOption[]>([]);
    private categoryGuardInitialized = false;

    get businessTypes(): FormArray {
        return this.form
            .get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)?.get(STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPES) as FormArray;
    }

    get metadataSuiteName() : FormControl {
        return this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)?.get(STEP_SETUP_FORM_GROUPS.SERVICE_METADATA)?.get(STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.SUITE_NAME) as FormControl;
    }

    get metadataSuiteDisplayName() : FormControl {
        return this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)?.get(STEP_SETUP_FORM_GROUPS.SERVICE_METADATA)?.get(STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.DISPLAY_NAME) as FormControl;
    }

    get category(): FormControl | null {
        return this.form
            ?.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)
            ?.get(STEP_SETUP_FORM_GROUPS.SERVICE_METADATA)
            ?.get(STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.CATEGORY) as FormControl | null;
    }

    ngOnInit() {
        this.form = <FormGroup>this.controlContainer.control;
        this.initControls();
        this.initSubscriptions();
    }

    initSubscriptions() {
        this.metadataSuiteName?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(this.onMetadataNameChange.bind(this));
        this.metadataSuiteDisplayName?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(this.onMetadataDisplayNameChange.bind(this));
    }

    initControls() {
        this.form.setControl(STEP_SETUP_FORM_GROUPS.STEP_SETUP, this.buildStepSetupGroup());
        this.updateAvailableBusinessTypeOptions();
        this.initCategoryOptions();
    }

    private initCategoryOptions(): void {
        const uci = this.appConfigService.getAppConfigModel()?.uciConfiguration;
        const keyMap = uci?.categorySubcategoryMapping;
        const metaMap = uci?.categorySubcategoryMetadataMapping;
        let categoryKeys: string[];
        let useLegacyOther: boolean;
        if (keyMap && metaMap) {
            categoryKeys = Object.keys(keyMap);
            useLegacyOther = false;
        } else {
            categoryKeys = ["AI", "AutonomousEnterprise", "CloudERPApps", "DataCloud", "Transformation", "Services"];
            useLegacyOther = true;
        }

        const options = this.buildCategoryOptionsFromKeys(categoryKeys, metaMap, useLegacyOther);
        this.categoryOptions.set(options);
        this.ensureCategoryFormControlDefault(options);
    }

    private buildCategoryOptionsFromKeys(
        categoryKeys: string[],
        metaMap?: Record<string, { displayName: string }>,
        includeLegacyOther = false
    ): CategoryOption[] {
        const uniqueKeys = includeLegacyOther
            ? Array.from(new Set([...categoryKeys, "Other"]))
            : Array.from(new Set(categoryKeys));
        const sorted = [...uniqueKeys.filter(k => k !== "Other"), ...uniqueKeys.filter(k => k === "Other")];
        return sorted.map((k) => ({
            key: k,
            label: metaMap?.[k]?.displayName ?? (ServiceMetadataCategory as Record<string, string>)[k] ?? k
        }));
    }

    private ensureCategoryFormControlDefault(options: CategoryOption[]): void {
        const categoryFormControl = this.category;

        if (!categoryFormControl) {
            return;
        }

        const availableKeys = new Set(options.map(o => o.key));
        const current = categoryFormControl.value as string | null | undefined;

        if (!current || !availableKeys.has(current)) {
            const defaultKey = availableKeys.has("Other") ? "Other" : (options[0]?.key ?? null);
            categoryFormControl.setValue(defaultKey, { emitEvent: false });
        }

        if (this.categoryGuardInitialized) {
            return;
        }
        this.categoryGuardInitialized = true;
        categoryFormControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(this.onCategoryOptionsChange.bind(this));
    }

    onCategoryOptionsChange(value: string) {
        if ((value === null || value === undefined || value === '') && this.categoryOptions().length > 0) {
            const options = this.categoryOptions();
            const availableKeys = new Set(options.map(o => o.key));
            const defaultKey = availableKeys.has("Other") ? "Other" : options[0].key;
            this.category.setValue(defaultKey, { emitEvent: false });
        }
    }

    private buildStepSetupGroup(): FormGroup {
        const businessTypesFormArray = this.fb.array([
            this.createBusinessTypeGroup(BusinessType.PRODUCTION)
        ], Validators.minLength(1));
        businessTypesFormArray.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(this.onBusinessTypeControlChange.bind(this));

        return this.fb.group({
            [STEP_SETUP_FORM_GROUPS.SERVICE_METADATA]: this.fb.group({
                [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.SUITE_NAME]: [
                    "", [Validators.required, CustomValidators.ValidatePattern(PRODUCT_BLUEPRINT_NAME_PATTERN, $localize`:YMSG|Unified wizard suite validation text@@organizations.unified-suite-step-setup.nameValidationError:Invalid input. The value can contain only lowercase alphabetical letters (a-z), numbers (0-9), hyphens (-), and periods (.). It must start and end with a lowercase letter.`), Validators.maxLength(15)],
                ],
                [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.NAME]: [
                    "", [Validators.required, CustomValidators.ValidateTechnicalName, Validators.maxLength(63)],
                ],
                [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.DISPLAY_NAME]: [
                    "", [Validators.required, Validators.maxLength(128)]
                ],
                [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.DESCRIPTION]: [
                    "", [Validators.required]
                ],
                [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.TENANT_PRODUCT_TYPE]: [
                    "", [
                        CustomValidators.ValidatePattern(
                            TENANT_PRODUCT_TYPE_PATTERN,
                            $localize`:YMSG|Tenant Product Type validation text@@organizations.unified-suite-step-setup.tenantProductTypeValidationError:Invalid input. The value can contain only: a-z, A-Z, 0-9, spaces, and the following characters: :.,-_#~!?@'"&$%+;[]`
                        ),
                        Validators.maxLength(100)
                    ]
                ],
                [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.DOCUMENTATION_URL]: [
                    "", [CustomValidators.ValidateStartsWith("https://", "Invalid URL format. The value must start with https:// and can contain any combination of characters after that.")]
                ],
                [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.ICON_URL]: [
                    ""
                ],

                [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.PACKAGE]: [
                    false
                ],
                [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.CATEGORY]: this.generatePropertyControl(
                    ResourceType.SERVICE_METADATA,
                    "productSuite.properties.category",
                    ServiceMetadataCategory.Other,
                    true
                )
            }),
            [STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPES]: businessTypesFormArray
        });

    }

    createBusinessTypeGroup(businessType: BusinessType): FormGroup {
        const {title, code, name} = this.generateBusinessTypeValues(businessType);
        const businessMapValues = Object.values(BUSINESS_TYPE_MAP);
        const disallowedTitles = businessMapValues.map(v => v.title);
        const disallowedNames = businessMapValues.map(v => v.name);
        const disallowedCodes = businessMapValues.map(v => v.code);

        const formGroup = this.fb.group({
            type: [{value: businessType, disabled: false}, Validators.required],
            title: [{
                value: title,
                disabled: !!title || !businessType
            }, [Validators.required,
                CustomValidators.ValidateDisallowValues([...disallowedTitles, ...disallowedNames], "You cannot manually enter this business type; choose it from the Business Type menu."),
                CustomValidators.ValidateDuplicatePropertyValidator(this.businessTypes, 'title', 'Already used in another business type in this suite')]],
            code: [{
                value: code,
                disabled: !!code || !businessType
            }, [Validators.required, CustomValidators.ValidatePattern(/^ZH\d+$/, "The code must begin with 'ZH' and be followed by numbers (example: ZH422)"),
                CustomValidators.ValidateDisallowValues(disallowedCodes, "You cannot manually enter this business type code; it is a reserved value"),
                CustomValidators.ValidateDuplicatePropertyValidator(this.businessTypes, 'code', 'Already used in another business type in this suite')]],
            name: [{
                value: name,
                disabled: !!name || !businessType
            }, [Validators.required]],
            btpBlueprint: this.fb.group({
                name: ["", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            }),
            [STEP_SETUP_FORM_GROUPS.RESOURCE_TYPE_DEFINITION]: this.createResourceTypeDefinitionGroup(this.dialogRef.data.path)
        });
        return this.registerBusinessTypeSubscriptions(formGroup);
    }

    private createResourceTypeDefinitionGroup(path: string): FormGroup {
        return this.fb.group({
            [STEP_SETUP_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.NAME]: ["", [Validators.required, Validators.maxLength(285), CustomValidators.ValidateTechnicalName]],
            [STEP_SETUP_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.GROUP]: this.generatePropertyControl(ResourceType.RTD, "group", WizardUtils.generateGroup(path)),
            [STEP_SETUP_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.VERSION]: this.generatePropertyControl(ResourceType.RTD, "version", "v1"),
            [STEP_SETUP_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.TYPE]: this.generatePropertyControl(ResourceType.RTD, "names.properties.type", "MySolution"),
            [STEP_SETUP_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.PLURAL]: this.generatePropertyControl(ResourceType.RTD, "names.properties.plural", "mysolutions"),
            [STEP_SETUP_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.SINGULAR]: this.generatePropertyControl(ResourceType.RTD, "names.properties.singular", "mysolution")
        });
    }

    private registerBusinessTypeSubscriptions(formGroup: FormGroup) {
        formGroup.get(STEP_SETUP_FORM_GROUPS.RESOURCE_TYPE_DEFINITION).get(STEP_SETUP_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.PLURAL)
            .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(this.onResourceTypeDefinitionPluralChange.bind(this, formGroup));
        formGroup.get(STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPE.TYPE).valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onBusinessTypeTypeChange.bind(this, formGroup));
        formGroup.get(STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPE.TITLE).valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onBusinessTypeTitleChange.bind(this, formGroup));
        return formGroup;
    }

    onResourceTypeDefinitionPluralChange(formGroup: FormGroup, val: string) {
        const rtdNameControl = formGroup?.get(STEP_SETUP_FORM_GROUPS.RESOURCE_TYPE_DEFINITION)?.get(STEP_SETUP_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.NAME);
        const metadataName = this.metadataSuiteName.value;
        const technicalName = WizardUtils.convertToTechnicalName(metadataName, "");
        const newPath = `${this.dialogRef.data.path}/${technicalName}`;
        const updatedGroup = WizardUtils.generateGroup(newPath);
        rtdNameControl?.setValue(`${val}.${updatedGroup}`);
    }

    onBusinessTypeTypeChange(formGroup: FormGroup, businessType: BusinessType): void {
        const {title, code, name} = this.generateBusinessTypeValues(businessType);
        formGroup.reset({
            type: businessType,
            title: {value: title, disabled: !!title},
            code: {value: code, disabled: !!code},
            name: {value: name, disabled: !!name}
        }, {emitEvent: false});

        this.updateBusinessTypeFormGroup(formGroup, this.metadataSuiteName.value);
        this.updateAvailableBusinessTypeOptions();
    }

    onBusinessTypeControlChange() {
        //Angular bug - some operations on FormArray controls (like push) do not mark the FormArray as dirty.
        this.businessTypes.markAsDirty();
        this.retriggerBusinessTypePropertyValidations();
    }

    retriggerBusinessTypePropertyValidations() {
        this.businessTypes.controls.forEach(c => {
            c.get("title")?.updateValueAndValidity({emitEvent: false});
            c.get("name")?.updateValueAndValidity({emitEvent: false});
            c.get("code")?.updateValueAndValidity({emitEvent: false});
        });
    }

    onBusinessTypeTitleChange(formGroup: FormGroup): void {
        this.updateBusinessTypeFormGroup(formGroup, this.metadataSuiteName.value);
    }

    generateBusinessTypeValues(businessType: BusinessType): BusinessTypeProperties {
        return BUSINESS_TYPE_MAP[businessType] || {title: null, code: null, name: null};
    }

    addBusinessType(): void {
        this.businessTypes.push(this.createBusinessTypeGroup(null));
        this.updateAvailableBusinessTypeOptions();
    }

    removeBusinessType(index: number): void {
        this.businessTypes.removeAt(index);
        this.updateAvailableBusinessTypeOptions();
    }

    onMetadataNameChange(value: string) {
        const stepSetupFormGroup = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)?.get(STEP_SETUP_FORM_GROUPS.SERVICE_METADATA);
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD);
        if (!payload) {
            return;
        }
        const technicalName = WizardUtils.convertToTechnicalName(value, "");

        stepSetupFormGroup.patchValue({
            [STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.NAME]: `${technicalName}-metadata`,
        });

        payload.patchValue({
            [PAYLOAD_FORM_CONTROLS.BASE_FOLDER_NAME]: technicalName,
            [PAYLOAD_FORM_CONTROLS.BASE_FOLDER_DISPLAY_NAME]: value,
            [PAYLOAD_FORM_CONTROLS.MANAGED_SERVICE_NAME]: `${technicalName}-ms`,
            [PAYLOAD_FORM_GROUPS.SERVICE_PUBLISH_CONFIGURATION]: {
                [PAYLOAD_FORM_CONTROLS.SERVICE_PUBLISH_CONFIGURATION.NAME]: `${technicalName}-publish`
            }
        }, {emitEvent: false}); // To avoid unnecessary change detection

        //Adjust the existing business types
        this.businessTypes.controls.forEach(control => this.updateBusinessTypeFormGroup(control, technicalName));
    }

    onMetadataDisplayNameChange(value: string) {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD);
        if (!payload) {
            return false;
        }

        payload.patchValue({
            [PAYLOAD_FORM_CONTROLS.BASE_FOLDER_DISPLAY_NAME]: value,
        }, {emitEvent: false}); // To avoid unnecessary change detection
        return true;
    }

    updateBusinessTypeFormGroup(formGroup: AbstractControl, suiteName: string) {
        const { path } = this.dialogRef.data;
        const newPath = `${path}/${suiteName}`;
        const controls = STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPE;

        const title = formGroup.get(controls.TITLE)?.value || '';
        const type = formGroup.get(controls.TYPE)?.value as BusinessType;
        const baseName = BUSINESS_TYPE_MAP[type]?.name || title;

        const cleanSuiteName = WizardUtils.cleanString(suiteName);
        const TECHNICAL_NAME_MAX_LENGTH = 63;
        const RTD_MAX_LENGTH = 32;
        const SOLUTIONS_LENGTH = "solutions".length;
        const allowedLength = RTD_MAX_LENGTH - SOLUTIONS_LENGTH - cleanSuiteName.length;

        const baseNameSlug = WizardUtils.sanitizeToSlug(baseName);
        const technicalName = WizardUtils.convertToTechnicalName(`${suiteName}-${baseNameSlug}`, "");

        const trimmedBaseNameSlug = WizardUtils.cleanString(baseNameSlug).slice(0, allowedLength);
        const suitePascal = WizardUtils.toPascalCase(suiteName);
        const basePascal = WizardUtils.toPascalCase(baseNameSlug).slice(0, allowedLength);
        const rtdType = `${suitePascal}${basePascal}Solution`;

        const singular = `${cleanSuiteName}${trimmedBaseNameSlug}solution`;
        const plural = `${cleanSuiteName}${trimmedBaseNameSlug}solutions`;
        const group = WizardUtils.generateGroup(newPath);
        const btpBlueprintTechnicalNameSuffix = '-blueprint';

        formGroup.patchValue({
            name: technicalName,
            btpBlueprint: {
                name: `${technicalName.slice(0, TECHNICAL_NAME_MAX_LENGTH - btpBlueprintTechnicalNameSuffix.length)}${btpBlueprintTechnicalNameSuffix}`
            },
            resourceTypeDefinition: {
                name: `${plural}.${group}`,
                group,
                version: "v1",
                type: rtdType,
                plural,
                singular
            }
        }, { emitEvent: false });
    }


    generatePropertyControl(resourceType: ResourceType, propertyPath: string, defaultValue: string, required = true) {
        const dialogData = this.dialogRef.data;
        const r = dialogData?.applicationResources.find((r: any) => r.type === resourceType);
        const spec = r?.versions?.[0]?.schema?.jsonSchema?.properties?.spec?.properties;
        const prop = this.nestedValuePipe.transform(spec, propertyPath);
        const control = Utils.getFormControl(prop, defaultValue, required);
        return control;
    }

    updateAvailableBusinessTypeOptions() {
        const selected = this.businessTypes.controls.map(c => c.get('type')?.value).filter(Boolean);

        const optionsPerControl = this.businessTypes.controls.map((control, index) => {
            const current = control.get('type')?.value;
            return this.filterBusinessTypeOptions(current, selected);
        });

        this.optionSets.set(optionsPerControl);
    }

    private filterBusinessTypeOptions(current: BusinessType, selected: BusinessType[]): KeyValue<string, BusinessType>[] {
        return ALL_BUSINESS_TYPE_OPTIONS.filter(option =>
            option.value === current ||
            option.value === BusinessType.OTHER ||
            !selected.includes(option.value)
        );
    }
}
